/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import killswitch from '@jira/platform__utils-feature-gates-killswitch/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { DEFAULT_OPTIONS } from '../../constants.tsx';
import type { Options } from '../../types.tsx';
import type { EntryPointActions, LoadActions } from '../types.tsx';

const PRELOAD_KILLSWITCH = 'entry_point_preload';

/**
 * @deprecated Use Platform EntryPoints API instead instead. https://go/platform-entry-points
 * */
export const useEntryPointActions = (
	{ loadEntryPoint, unloadEntryPoint, commitPreload }: LoadActions,
	{
		preloadMaxAge = fg('jira_relay_presist_entrypoint_preloaded_data')
			? DEFAULT_OPTIONS.longerPreloadMaxAge
			: DEFAULT_OPTIONS.preloadMaxAge,
		timeToIntent = DEFAULT_OPTIONS.timeToIntent,
	}: Options,
	startMetric: (preloadTime?: number) => void,
): EntryPointActions => {
	const preloadIntentTimeout = useRef<ReturnType<typeof setTimeout>>();
	const preloadMaxAgeTimeout = useRef<ReturnType<typeof setTimeout>>();
	const preloadLastRequested = useRef<number>(0);

	const cancelPreload = useCallback(() => {
		if (!killswitch(PRELOAD_KILLSWITCH)) {
			clearTimeout(preloadIntentTimeout.current);
			clearTimeout(preloadMaxAgeTimeout.current);
		}
	}, []);

	const updateLastRequested = useCallback(() => {
		preloadLastRequested.current = 0;
		preloadMaxAgeTimeout.current = undefined;
	}, []);

	const load = useCallback(() => {
		// eslint-disable-next-line jira/jira-ssr/no-server-variable
		if (__SERVER__ && fg('jfp_magma_jira-entrypoint-actions-load')) {
			// prevent "unconnected" entrypoint loading at SSR
			return;
		}
		cancelPreload();
		startMetric(preloadLastRequested.current);

		if (preloadLastRequested.current > 0) {
			commitPreload();
		} else {
			loadEntryPoint();
		}

		if (fg('jira_relay_presist_entrypoint_preloaded_data') === false) {
			updateLastRequested();
		}
	}, [cancelPreload, commitPreload, loadEntryPoint, startMetric, updateLastRequested]);

	const preload = useCallback(() => {
		if (killswitch(PRELOAD_KILLSWITCH)) {
			return;
		}

		// eslint-disable-next-line jira/jira-ssr/no-server-variable
		if (__SERVER__ && fg('jfp_magma_jira-entrypoint-actions-load')) {
			// prevent "unconnected" entrypoint loading at SSR
			return;
		}

		cancelPreload();

		preloadIntentTimeout.current = setTimeout(() => {
			if (
				typeof preloadMaxAgeTimeout.current === 'number' &&
				preloadMaxAgeTimeout.current > 0 &&
				fg('jira_relay_presist_entrypoint_preloaded_data') === false
			) {
				return;
			}

			preloadLastRequested.current = Date.now();

			loadEntryPoint({ isPreload: true });

			preloadMaxAgeTimeout.current = setTimeout(updateLastRequested, preloadMaxAge);
		}, timeToIntent);
	}, [cancelPreload, loadEntryPoint, preloadMaxAge, timeToIntent, updateLastRequested]);

	useEffect(() => cancelPreload, [cancelPreload]);

	return useMemo<EntryPointActions>(
		() => ({
			cancelPreload,
			load,
			preload,
			unload: unloadEntryPoint,
		}),
		[cancelPreload, load, preload, unloadEntryPoint],
	);
};
